/**
 * Startup Subscription Screen
 *
 * Full subscription management for startups including tier comparison,
 * billing management, and upgrade/downgrade flows.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { View, Text, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import {
  Sparkles,
  Shield,
  Zap,
  HelpCircle,
} from 'lucide-react-native';
import { useRouter } from 'expo-router';
import { track, useTrackScreen } from '@/lib/analytics';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAlert } from '@/components/AlertDialog';

// Breadcrumbs for navigation
const BREADCRUMBS: Breadcrumb[] = [
  { label: 'Company Profile', href: '/(startup)/company' },
  { label: 'Subscription' },
];
import { PricingCard, BillingToggle, SubscriptionStatus, ReferralCodeInput, CancellationFlow, ConfirmationModal } from '@/components/subscription';
import { useSubscription } from '@/lib/stripe';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { useProfileStore } from '@/lib/state/profile-store';
import type { BillingInterval, StartupTier } from '@/lib/subscription/types';
import { STARTUP_TIERS } from '@/lib/subscription/types';
import { ANNUAL_DISCOUNT_PERCENT } from '@/lib/subscription/pricing';
import { handleError } from '@/lib/errors';

// ============================================================================
// Main Component
// ============================================================================

export default function StartupSubscriptionScreen() {
  useTrackScreen('StartupSubscription');
  const router = useRouter();
  const colors = useThemeColors();
  const { width } = useWindowDimensions();
  const { error: showError } = useAlert();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('month');

  // Modal state
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    tier: StartupTier | null;
    isUpgrade: boolean;
  }>({
    visible: false,
    tier: null,
    isUpgrade: false,
  });

  // Cancellation flow state
  const [showCancellationFlow, setShowCancellationFlow] = useState(false);

  // Auth user — used for the referral code input
  const userId = useProfileStore((s) => s.user?.id) ?? '';

  // Responsive layout: 2 columns on real desktop only (width >= 900px) — at
  // tablet width (768-900px) cards get cramped, so we keep them stacked.
  const isDesktop = width >= 900;
  const cardWidth = isDesktop ? (width - 80) / 2 : undefined; // 80 = padding (20*2) + gap (40)

  const {
    subscription,
    currentTier,
    isLoading,
    error,
    subscribe,
    upgrade,
    downgrade,
    cancel,
    reactivate,
    openBillingPortal,
    retryPayment,
    clearError,
  } = useSubscription();

  // Track pricing_viewed on mount
  const pricingTracked = useRef(false);
  useEffect(() => {
    if (pricingTracked.current) return;
    pricingTracked.current = true;
    track('pricing_viewed', { userType: 'startup' });
  }, []);

  // Load subscription from database on mount
  const loadSubscription = useSubscriptionStore((s) => s.loadSubscription);
  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  // Process subscription changes (defined before handleSelectTier since it's used as dependency)
  const processSubscriptionChange = useCallback(
    async (tier: StartupTier) => {
      try {
        if (!subscription || currentTier === 'free') {
          // New subscription
          await subscribe(tier, billingInterval);
          track('subscription_started', { plan: tier, userType: 'startup' });
        } else if (tier === 'free') {
          // Downgrade to free = cancel
          await cancel();
        } else {
          // Upgrade or downgrade
          const isUpgrade = STARTUP_TIERS[tier].monthlyPrice > STARTUP_TIERS[currentTier as StartupTier].monthlyPrice;

          const result = isUpgrade
            ? await upgrade(tier)
            : await downgrade(tier);

          if (!result.success && result.error) {
            showError('Error', result.error);
          }
        }
      } catch (err) {
        handleError(err, { context: 'StartupSubscription.handleSubscriptionChange' });
      }
    },
    [subscription, currentTier, billingInterval, subscribe, cancel, upgrade, downgrade, showError]
  );

  const handleSelectTier = useCallback(
    (tier: StartupTier) => {
      if (tier === currentTier) {
        return;
      }

      const currentTierPrice = currentTier === 'free' ? 0 : STARTUP_TIERS[currentTier as StartupTier]?.monthlyPrice ?? 0;
      const newTierPrice = tier === 'free' ? 0 : STARTUP_TIERS[tier].monthlyPrice;
      const isUpgrade = newTierPrice > currentTierPrice;

      // Show confirmation modal
      setConfirmModal({
        visible: true,
        tier,
        isUpgrade,
      });
    },
    [currentTier]
  );

  const handleConfirmTierChange = useCallback(async () => {
    if (!confirmModal.tier) return;

    setConfirmModal((prev) => ({ ...prev, visible: false }));
    await processSubscriptionChange(confirmModal.tier);
  }, [confirmModal.tier, processSubscriptionChange]);

  const handleCloseModal = useCallback(() => {
    setConfirmModal({
      visible: false,
      tier: null,
      isUpgrade: false,
    });
  }, []);

  const handleCancel = useCallback(() => {
    setShowCancellationFlow(true);
  }, []);

  const handleCancellationFlowCancel = useCallback(async (reason: string, feedback?: string) => {
    await cancel({ reason, feedback });
  }, [cancel]);

  const handleCancellationFlowClose = useCallback(() => {
    setShowCancellationFlow(false);
  }, []);

  const tiers = Object.values(STARTUP_TIERS);

  return (
    <AppLayout title="Subscription" breadcrumbs={BREADCRUMBS}>
      <SafeAreaView edges={isWeb ? [] : ['top']} style={{ flex: 1, backgroundColor: colors.surface.secondary }}>
      <ScrollView
        style={{ flex: 1, backgroundColor: colors.surface.secondary }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ padding: 20, paddingBottom: 60 }}
      >
            {/* Header */}
            <Animated.View
              entering={FadeInDown.delay(100).duration(400)}
              style={styles.header}
            >
              <View style={styles.headerText}>
                <Text style={[styles.title, { color: colors.text.primary }]}>
                  Choose Your Plan
                </Text>
                <Text style={[styles.subtitle, { color: colors.text.secondary }]}>
                  Unlock features to accelerate your fundraising
                </Text>
              </View>
            </Animated.View>

            {/* Current Status */}
            <Animated.View
              entering={FadeInUp.delay(150).duration(400)}
              style={styles.section}
            >
              <SubscriptionStatus
                subscription={subscription}
                userRole="startup"
                onManageBilling={openBillingPortal}
                onRetryPayment={retryPayment}
                onReactivate={reactivate}
              />
            </Animated.View>

            {/* Billing Toggle */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(400)}
              style={styles.toggleSection}
            >
              <BillingToggle
                value={billingInterval}
                onChange={setBillingInterval}
              />
            </Animated.View>

            {/* Referral Code Input */}
            <Animated.View
              entering={FadeInUp.delay(250).duration(400)}
              style={styles.section}
            >
              <ReferralCodeInput
                userId={userId}
                userType="startup"
                disabled={isLoading}
              />
            </Animated.View>

            {/* Pricing Cards */}
            <View style={[styles.cardsContainer, isDesktop && styles.cardsContainerDesktop]}>
              {tiers.map((tier, index) => {
                const currentTierPrice =
                  currentTier === 'free'
                    ? 0
                    : STARTUP_TIERS[currentTier as StartupTier]?.monthlyPrice ?? 0;

                return (
                  <View
                    key={tier.id}
                    style={[
                      styles.cardWrapper,
                      isDesktop && { width: cardWidth, maxWidth: 400 },
                    ]}
                  >
                    <PricingCard
                      tier={tier}
                      billingInterval={billingInterval}
                      isCurrentPlan={currentTier === tier.id}
                      currentTierPrice={currentTierPrice}
                      onSelect={() => handleSelectTier(tier.id)}
                      disabled={isLoading}
                      index={index}
                      equalHeight={isDesktop}
                    />
                  </View>
                );
              })}
            </View>

            {/* Confirmation Modal for Tier Changes */}
            <ConfirmationModal
              visible={confirmModal.visible}
              title={
                confirmModal.isUpgrade
                  ? 'Upgrade Your Plan'
                  : 'Change Your Plan'
              }
              tierName={
                confirmModal.tier
                  ? STARTUP_TIERS[confirmModal.tier].name
                  : undefined
              }
              message={
                confirmModal.isUpgrade
                  ? 'Unlock more features and accelerate your fundraising journey.'
                  : 'Your plan will be updated and you will be credited for any unused time.'
              }
              confirmText={
                confirmModal.isUpgrade
                  ? 'Upgrade Now'
                  : 'Change Plan'
              }
              cancelText="Not Now"
              isUpgrade={confirmModal.isUpgrade}
              onConfirm={handleConfirmTierChange}
              onCancel={handleCloseModal}
            />

            {/* Cancellation Flow Modal */}
            <CancellationFlow
              visible={showCancellationFlow}
              currentTier={subscription?.tier || 'free'}
              currentPeriodEnd={subscription?.currentPeriodEnd}
              onCancel={handleCancellationFlowCancel}
              onClose={handleCancellationFlowClose}
              discountPercent={30}
            />

            {/* Benefits Section */}
            <Animated.View
              entering={FadeInUp.delay(400).duration(400)}
              style={styles.section}
            >
              <View
                style={[
                  styles.benefitsCard,
                  {
                    backgroundColor: colors.surface.primary,
                    borderColor: colors.border.light,
                  },
                ]}
              >
                <Text style={[styles.benefitsTitle, { color: colors.text.primary }]}>
                  Why upgrade?
                </Text>

                <View style={styles.benefitsList}>
                  <View style={styles.benefitRow}>
                    <View
                      style={[
                        styles.benefitIcon,
                        { backgroundColor: colors.primary[50] },
                      ]}
                    >
                      <Sparkles size={18} color={colors.primary[600]} />
                    </View>
                    <View style={styles.benefitContent}>
                      <Text style={[styles.benefitName, { color: colors.text.primary }]}>
                        Complete Analysis
                      </Text>
                      <Text style={[styles.benefitDesc, { color: colors.text.secondary }]}>
                        Get full 15-dimension scoring to understand every aspect of your readiness
                      </Text>
                    </View>
                  </View>

                  <View style={styles.benefitRow}>
                    <View
                      style={[
                        styles.benefitIcon,
                        { backgroundColor: colors.primary[50] },
                      ]}
                    >
                      <Zap size={18} color={colors.primary[600]} />
                    </View>
                    <View style={styles.benefitContent}>
                      <Text style={[styles.benefitName, { color: colors.text.primary }]}>
                        Direct Access
                      </Text>
                      <Text style={[styles.benefitDesc, { color: colors.text.secondary }]}>
                        Message investors directly and get your pitch in front of the right people
                      </Text>
                    </View>
                  </View>

                  <View style={styles.benefitRow}>
                    <View
                      style={[
                        styles.benefitIcon,
                        { backgroundColor: colors.primary[50] },
                      ]}
                    >
                      <Shield size={18} color={colors.primary[600]} />
                    </View>
                    <View style={styles.benefitContent}>
                      <Text style={[styles.benefitName, { color: colors.text.primary }]}>
                        Team Collaboration
                      </Text>
                      <Text style={[styles.benefitDesc, { color: colors.text.secondary }]}>
                        Add team members to manage investor relations together
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            </Animated.View>

            {/* FAQ Link */}
            <Animated.View
              entering={FadeInUp.delay(500).duration(400)}
              style={styles.section}
            >
              <AnimatedPressable
                onPress={() => router.push('/(startup)/help')}
                style={[
                  styles.faqButton,
                  {
                    backgroundColor: colors.surface.primary,
                    borderColor: colors.border.light,
                  },
                ]}
              >
                <HelpCircle size={20} color={colors.text.secondary} />
                <Text style={[styles.faqText, { color: colors.text.secondary }]}>
                  Questions? Visit our Help Center
                </Text>
              </AnimatedPressable>
            </Animated.View>

            {/* Cancel Option */}
            {subscription && subscription.status !== 'canceled' && !subscription.cancelAtPeriodEnd && (
              <Animated.View
                entering={FadeInUp.delay(550).duration(400)}
                style={styles.section}
              >
                <AnimatedPressable onPress={handleCancel} disabled={isLoading}>
                  <Text style={[styles.cancelLink, { color: colors.text.tertiary }]}>
                    Cancel subscription
                  </Text>
                </AnimatedPressable>
              </Animated.View>
            )}

            {/* Refund Policy — beside cancellation, where the question arises */}
            <View style={styles.section}>
              <Text style={[styles.policyNote, { color: colors.text.tertiary }]}>
                Cancelling keeps your plan until the end of the period you have paid for.{' '}
                <Text
                  accessibilityRole="link"
                  onPress={() => router.push('/refunds')}
                  style={[styles.cancelLink, { color: colors.text.secondary }]}
                >
                  Refund Policy
                </Text>
              </Text>
            </View>

            {/* Bottom Padding */}
            <View style={{ height: 40 }} />
          </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  safeArea: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
  },
  header: {
    marginBottom: 20,
  },
  headerText: {
    alignItems: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 22,
  },
  section: {
    marginBottom: 20,
  },
  toggleSection: {
    alignItems: 'center',
    marginBottom: 24,
  },
  cardsContainer: {
    gap: 20,
    marginBottom: 24,
  },
  cardsContainerDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: 24,
  },
  cardWrapper: {
    marginTop: 12, // Space for badges
  },
  benefitsCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
  },
  benefitsTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 16,
  },
  benefitsList: {
    gap: 16,
  },
  benefitRow: {
    flexDirection: 'row',
    gap: 14,
  },
  benefitIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  benefitContent: {
    flex: 1,
  },
  benefitName: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  benefitDesc: {
    fontSize: 13,
    lineHeight: 18,
  },
  faqButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
  },
  faqText: {
    fontSize: 14,
    fontWeight: '500',
  },
  cancelLink: {
    fontSize: 14,
    textAlign: 'center',
    textDecorationLine: 'underline',
  },
  policyNote: {
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
});
